/* eslint-disable no-shadow */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useState, useEffect, useRef } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { batch, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import { Input, RankingItem, RankingItemWrapper } from './ranking.styled';
import { FormContext } from '../../../customise/contexts/form-context';
import { setUserInputOnCtrl } from '../../../form/reducer/form';
import { whenSideBySide } from '../fields-customise.style';
import { SubtextWrapper } from '../../common-field-styles';
import { ControlType } from '../../../../shared/form-lib';
import { ControlPropType } from '../../ctrl_proptype';

const DragRanking = ({ ctrl, formDesign }) => {
  const dispatch = useDispatch();
  const { currentPageNo, inCustomise, inBuilder } = useContext(FormContext);
 
  const [otherText, setOtherText] = useState(ctrl.user_option || '');
  const [list, setList] = useState(
    ctrl.props.other.enabled ? [...ctrl.props.options, ControlType.OptOther.STRING] : ctrl.props.options
  );

  const saveUpdatedCtrl = (property, newValue) => {
    dispatch(
      setUserInputOnCtrl({
        newValue,
        property,
        page: currentPageNo - 1,
        ctrlId: ctrl.internal_id,
        isValid: true,
      })
    );
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = [...list];
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    saveUpdatedCtrl('ranking_options', items);
    setList(items);
    const newItem = [...items];
    const index = newItem.indexOf(ControlType.OptOther.STRING);
    if (index > -1) newItem[index] = ctrl.user_option ?? '';
    saveUpdatedCtrl('value', newItem);
  };

  const handleMaintainOrder = (userOption) => {
    const orderedValue = ctrl.ranking_options ? [...ctrl.ranking_options] : [...ctrl.props.options];
    if (ctrl.props.other.enabled) {
      const index = orderedValue.indexOf(ControlType.OptOther.STRING);
      if (index > -1) {
        orderedValue[index] = userOption ?? '';
      } else {
        orderedValue.push(userOption ?? '');
      }
    }

    return orderedValue;
  };

  const saveEditOtherText = useRef(
    debounce((orderedValue, newValue) => {
      batch(() => {
        saveUpdatedCtrl('value', orderedValue);
        saveUpdatedCtrl('user_option', newValue);
      });
    }, 800)
  );

  const handleTextChange = (e) => {
    if (e.target.value === ctrl.user_option) return;
    setOtherText(e.target.value);
    const orderedValue = handleMaintainOrder(e.target.value);
    saveEditOtherText.current(orderedValue, e.target.value);
  };

  useEffect(() => {
    const defaultValues = handleMaintainOrder(ctrl.user_option);
    saveUpdatedCtrl('value', defaultValues);
    // No need to include ctrl.user_option, saveUpdatedCtrl and/or handleMaintainOrder in the dependency array
  }, []);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={`ranking-${ctrl.internal_id}`} type={`ranking-${ctrl.internal_id}`}>
        {(provided) => (
          <RankingItemWrapper
            {...provided.droppableProps}
            ref={provided.innerRef}
            id={`fpl-${ctrl.internal_id}`}
            className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
            {(ctrl.ranking_options || list).map((elem, index) => {
              const id = `${elem}-${index}`;
              return (
                <div key={id}>
                  {elem === ControlType.OptOther.STRING ? (
                    <Draggable draggableId={id} key={id} index={index} type={`ranking-${ctrl.internal_id}`}>
                      {(provided, snapshot) => (
                        <RankingItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          isDragging={snapshot.isDragging}
                          notInBuilder={!inBuilder}>
                          <span>
                            <img src="https://storage.googleapis.com/fplsblog/drag-icon.svg" alt="drag handle" />
                          </span>
                          <span>{index + 1}.</span>
                          <Input
                            placeholder="Click to add your option"
                            value={otherText}
                            notInBuilder={!inBuilder}
                            disabled={ctrl.props.readonly || inCustomise || inBuilder}
                            onChange={handleTextChange}
                          />
                        </RankingItem>
                      )}
                    </Draggable>
                  ) : (
                    <Draggable key={id} draggableId={id} index={index} type={`ranking-${ctrl.internal_id}`}>
                      {(provided, snapshot) => (
                        <RankingItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          isDragging={snapshot.isDragging}
                          notInBuilder={!inBuilder}>
                          <span>
                            <img src="https://storage.googleapis.com/fplsblog/drag-icon.svg" alt="drag handle" />
                          </span>
                          <span>{index + 1}.</span>
                          {elem}
                        </RankingItem>
                      )}
                    </Draggable>
                  )}
                </div>
              );
            })}
            {provided.placeholder}
            <SubtextWrapper id="descriptive text">{ctrl.props.subtext}</SubtextWrapper>
          </RankingItemWrapper>
        )}
      </Droppable>
    </DragDropContext>
  );
};

DragRanking.defaultProps = {
  formDesign: null,
};

DragRanking.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};
export default DragRanking;
